import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Eye, Database, FileText, ToggleLeft, ToggleRight, List, File } from 'lucide-react';
import StatusIcon from './StatusIcon';
import DataTable from './DataTable';
import { colors } from '../theme/colors';

// Reusable Progress Bar Component (kept for other uses)
const ProgressBar = ({ value, width = '120px', showText = true, textPrefix = '' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <div style={{ 
      width, 
      height: '8px', 
      backgroundColor: '#e5e7eb', 
      borderRadius: '3px',
      overflow: 'hidden'
    }}>
      <div style={{
        height: '100%',
        backgroundColor: value >= 80 ? '#16a34a' : value >= 60 ? '#f59e0b' : '#ef4444',
        width: `${value}%`,
        transition: 'width 0.3s ease'
      }} />
    </div>
    {showText && (
      <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '45px' }}>
        {textPrefix}{value}%
      </span>
    )}
  </div>
);

// Neutral Data Slider Component (for System Files page)
const DataSlider = ({ value, width = '120px', showText = true, textPrefix = '' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <div style={{ 
      width, 
      height: '6px', 
      backgroundColor: '#f3f4f6', 
      borderRadius: '3px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{
        height: '100%',
        backgroundColor: '#6b7280', // Neutral gray color
        width: `${value}%`,
        transition: 'width 0.3s ease',
        borderRadius: '2px'
      }} />
    </div>
    {showText && (
      <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '45px' }}>
        {textPrefix}{value}%
      </span>
    )}
  </div>
);

// Generate abbreviation from filename
const generateAbbreviation = (filename) => {
  // Remove file extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  
  // Split by various separators and camelCase/PascalCase
  let words = [];
  
  // First, split by common separators (spaces, hyphens, underscores, dots)
  const separatorSplit = nameWithoutExt.split(/[\s\-_\.]+/).filter(word => word.length > 0);
  
  // For each word from separator split, also split by camelCase/PascalCase
  separatorSplit.forEach(word => {
    // Split camelCase/PascalCase: "PortfolioScrub" -> ["Portfolio", "Scrub"]
    const camelSplit = word.split(/(?=[A-Z])/).filter(part => part.length > 0);
    words.push(...camelSplit);
  });
  
  // If no words found from separators, try to split camelCase/PascalCase on the whole string
  if (words.length === 0) {
    words = nameWithoutExt.split(/(?=[A-Z])/).filter(word => word.length > 0);
  }
  
  // Take first letter of first 2 words only
  const abbreviation = words
    .slice(0, 2)
    .map(word => word.substring(0, 1).toUpperCase())
    .join('');
  
  return abbreviation || 'UF'; // Fallback to UF if no words found
};

// Generate color based on filename hash for consistency
const generateColor = (filename) => {
  const colors = [
    '#3b82f6', // Blue
    '#10b981', // Green  
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#ec4899', // Pink
    '#6366f1'  // Indigo
  ];
  
  let hash = 0;
  for (let i = 0; i < filename.length; i++) {
    hash = ((hash << 5) - hash + filename.charCodeAt(i)) & 0xffffffff;
  }
  return colors[Math.abs(hash) % colors.length];
};

// Styled Components
const FileTypeIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%; /* Make it circular */
  background: ${props => props.color};
  color: white;
  font-size: 0.6rem;
  font-weight: 600;
  margin-right: 0.25rem;
  cursor: help;
  transition: transform 0.2s;
  border: 2px solid white; /* White border for separation when overlapping */
  
  &:hover {
    transform: scale(1.1);
    z-index: 10; /* Bring hovered icon to front */
  }
`;

const SourceFileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PercentageText = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
`;

// Helper function to render file type icon
const renderFileTypeIcon = (file) => {
  const abbreviation = generateAbbreviation(file.name);
  const color = generateColor(file.name);
  const tooltipText = `${file.name} (${file.percentage}%)\n${file.type_description || 'File'}`;
  
  return (
    <SourceFileContainer>
      <FileTypeIcon 
        color={color}
        title={tooltipText}
      >
        {abbreviation}
      </FileTypeIcon>
    </SourceFileContainer>
  );
};

// Helper function to render loan source files as icons
const renderLoanSourceFiles = (loan) => {
  if (!loan.source_files || !loan.record_types) {
    return <span style={{ color: '#9ca3af' }}>-</span>;
  }
  
  const sourceFileNames = loan.source_files.split(', ').filter(name => name.trim());
  const recordTypes = loan.record_types.split(', ').filter(type => type.trim());
  
  if (sourceFileNames.length === 0) {
    return <span style={{ color: '#9ca3af' }}>-</span>;
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      position: 'relative',
      height: '28px', /* Fixed height to contain overlapping circles */
      width: `${Math.min(sourceFileNames.length * 18 + 10, 120)}px` /* Dynamic width based on count, max 120px */
    }}>
      {sourceFileNames.map((fileName, index) => {
        const recordType = recordTypes[index] || 'unknown';
        const abbreviation = generateAbbreviation(fileName);
        const color = generateColor(fileName);
        const tooltipText = `${fileName}\n${getFileTypeDescription(recordType)}`;
        
        return (
          <FileTypeIcon 
            key={index}
            color={color}
            title={tooltipText}
            style={{
              position: 'absolute',
              left: `${index * 18}px`, /* Overlap by only showing 18px of each circle */
              zIndex: sourceFileNames.length - index, /* Reverse z-index so first items are on top */
              margin: 0 /* Remove default margin for absolute positioning */
            }}
          >
            {abbreviation}
          </FileTypeIcon>
        );
      })}
    </div>
  );
};

// Helper function to get file type description (matching backend function)
const getFileTypeDescription = (recordType) => {
  const typeDescriptions = {
    'portfolio_scrub': 'Portfolio Scrub Report',
    'master_file': 'Master File',
    'proposal': 'Proposal Document',
    'security': 'Security Document',
    'role_report': 'Role Report',
    'loan': 'Individual Loan File',
    'unknown': 'Unknown File Type'
  };
  return typeDescriptions[recordType] || recordType.title();
};

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0 1.5rem;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: ${props => props.active ? colors.tabActive : 'white'};
  color: ${props => props.active ? 'white' : colors.tab};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? colors.tabHover : '#f9fafb'};
    border-color: ${props => props.active ? colors.tabHover : '#9ca3af'};
  }
`;


const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.success {
    background: #dcfce7;
    color: #166534;
  }
`;


// Main component
const SystemFilesList = ({ 
  records = [], 
  fields = [], 
  onView, 
  initialViewMode = 'loans',
  searchTerm
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode); // 'loans' or 'fields'
  
  // Filter data based on search term
  
  const filteredRecords = records.filter(record => 
    !searchTerm || 
    (record.loan_id && record.loan_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (record.borrower_name && record.borrower_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const filteredFields = fields.filter(field =>
    !searchTerm || field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  

  return (
    <div>
      {/* View Toggle */}
      <ViewToggle>
        <ToggleButton 
          active={viewMode === 'loans'} 
          onClick={() => setViewMode('loans')}
        >
          <Database size={16} />
          Loan View
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'fields'} 
          onClick={() => setViewMode('fields')}
        >
          <FileText size={16} />
          Field View
        </ToggleButton>
      </ViewToggle>


      {/* Loan-centric View */}
      {viewMode === 'loans' && (
        <div style={{ marginLeft: '1.5rem', marginRight: '1.5rem', marginBottom: '1.5rem' }}>
          <DataTable
            pagination={true}
            pageSize={50}
            pageSizeOptions={[50, 100, 250]}
            columns={[
              {
                header: 'Loan ID',
                field: 'loan_id',
                width: 'minmax(100px, 140px)',
                cellStyle: { fontWeight: '600', fontFamily: 'monospace' }
              },
              {
                header: 'Borrower',
                field: 'borrower_name',
                width: 'minmax(200px, 2fr)',
                cellStyle: { fontWeight: '500' },
                render: (loan) => loan.borrower_name || '-'
              },
              {
                header: 'Sources',
                field: 'source_files',
                width: 'minmax(120px, 180px)',
                headerStyle: { justifyContent: 'flex-start' },
                cellStyle: { justifyContent: 'flex-start', paddingLeft: '1rem' },
                render: (loan) => renderLoanSourceFiles(loan)
              },
              {
                header: 'Field Values (#)',
                field: 'fields',
                width: 'minmax(250px, 1fr)',
                render: (loan) => {
                  const completionRate = Math.round((loan.found_fields / loan.total_fields) * 100);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <DataSlider value={completionRate} width="120px" showText={false} />
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '45px' }}>
                        {loan.found_fields}/{loan.total_fields}
                      </span>
                    </div>
                  );
                }
              },
              {
                header: ' ',
                field: 'actions',
                width: 'minmax(48px, 60px)',
                headerStyle: { justifyContent: 'center' },
                cellStyle: { justifyContent: 'center' },
                render: (loan) => (
                  <ActionButton onClick={() => onView?.(loan.loan_id)} title="View loan details">
                    <List size={14} />
                  </ActionButton>
                )
              }
            ]}
                  data={filteredRecords}
            keyField="loan_id"
            onRowClick={(loan) => onView?.(loan.loan_id)}
          />
        </div>
      )}

      {/* Field-centric View - 2 Column Split */}
      {viewMode === 'fields' && (
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          marginLeft: '1.5rem',
          marginRight: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Left Column */}
          <div style={{ flex: '1', minWidth: '0' }}>
            <DataTable
              columns={[
                {
                  header: 'Field Name',
                  field: 'name',
                  width: 'minmax(200px, 2fr)',
                  cellStyle: { fontWeight: '500' }
                },
                {
                  header: 'Found',
                  field: 'completion',
                  width: 'minmax(150px, 1fr)',
                  render: (field) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <DataSlider value={field.completion_rate} width="60px" showText={false} />
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '35px' }}>
                        {field.found_count.toLocaleString()}
                      </span>
                    </div>
                  )
                },
                {
                  header: '',
                  field: 'source_files',
                  width: 'minmax(30px, 40px)',
                  render: (field) => {
                    if (field.source_files.length === 0) {
                      return <span style={{ color: '#9ca3af' }}>-</span>;
                    } else {
                      // Create a fake loan object with source_files string for overlapping circles
                      const fakeLohan = {
                        source_files: field.source_files.map(f => f.name).join(', '),
                        record_types: 'loan'
                      };
                      return renderLoanSourceFiles(fakeLohan);
                    }
                  }
                },
                {
                  header: '',
                  field: 'matches',
                  width: 'minmax(40px, 50px)',
                  headerStyle: { justifyContent: 'center' },
                  cellStyle: { justifyContent: 'center' },
                  render: (field) => (
                    <ActionButton onClick={() => console.log('View matches for field:', field.name)}>
                      <List size={14} />
                    </ActionButton>
                  )
                }
              ]}
                    data={filteredFields.slice(0, Math.ceil(filteredFields.length / 2))}
              keyField="name"
            />
          </div>

          {/* Right Column */}
          <div style={{ flex: '1', minWidth: '0' }}>
            <DataTable
              columns={[
                {
                  header: 'Field Name',
                  field: 'name',
                  width: 'minmax(200px, 2fr)',
                  cellStyle: { fontWeight: '500' }
                },
                {
                  header: 'Found',
                  field: 'completion',
                  width: 'minmax(150px, 1fr)',
                  render: (field) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <DataSlider value={field.completion_rate} width="60px" showText={false} />
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '35px' }}>
                        {field.found_count.toLocaleString()}
                      </span>
                    </div>
                  )
                },
                {
                  header: '',
                  field: 'source_files',
                  width: 'minmax(30px, 40px)',
                  render: (field) => {
                    if (field.source_files.length === 0) {
                      return <span style={{ color: '#9ca3af' }}>-</span>;
                    } else {
                      // Create a fake loan object with source_files string for overlapping circles
                      const fakeLohan = {
                        source_files: field.source_files.map(f => f.name).join(', '),
                        record_types: 'loan'
                      };
                      return renderLoanSourceFiles(fakeLohan);
                    }
                  }
                },
                {
                  header: '',
                  field: 'matches',
                  width: 'minmax(40px, 50px)',
                  headerStyle: { justifyContent: 'center' },
                  cellStyle: { justifyContent: 'center' },
                  render: (field) => (
                    <ActionButton onClick={() => console.log('View matches for field:', field.name)}>
                      <List size={14} />
                    </ActionButton>
                  )
                }
              ]}
                    data={filteredFields.slice(Math.ceil(filteredFields.length / 2))}
              keyField="name"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemFilesList;
